// Bottle fraction counting — the visual "how full is the bottle" slider.
//
// Liquor and wine are counted in bottles, and the fractional part of a count
// is an eyeball estimate of the fill level of one open bottle. This module
// owns the two pure questions behind the feature:
//
//   1. Is this count line a liquor/wine line? Decided from the item's
//      CATEGORY text alone — every variation a restaurant actually uses
//      ("Wine - Red", "Spirits", "Well Liquor", "Champagne & Sparkling") —
//      so no AI call is ever involved. Beer is deliberately excluded: beer is
//      counted in whole cans/bottles, never in tenths of a fill level.
//
//   2. The tenths math. The slider replaces the DECIMAL portion of the count
//      and leaves the whole bottles alone: 5 + 0.7 → "5.7", nothing counted
//      + 0.9 → "0.9", a full bottle (10/10) bumps the whole number.
//
// Pure (no React / no I/O) so it is unit-testable and mirrors can be checked.

// ── Text helpers (same approach as utils/alcoholParRules.mjs) ───────────────
function normalize(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents: rosé -> rose
    .toLowerCase()
    .replace(/-/g, ' ') // hyphens are separators: "non-alcoholic" -> "non alcoholic"
    .replace(/[^a-z0-9/.\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenSet(haystack) {
  return new Set(normalize(haystack).split(/[\s/.-]+/).filter(Boolean));
}

// Single short words ("gin", "rum", "port") must match a whole token so
// "ginger" / "drum" / "import" never trip them. Multi-word or digit-bearing
// phrases ("triple sec", "by the glass") match as substrings of the
// normalized text instead.
function makeMatcher(haystack) {
  const norm = normalize(haystack);
  const words = tokenSet(haystack);
  return (keyword) => {
    const k = normalize(keyword);
    if (!k) return false;
    if (/[\s/]/.test(k) || /\d/.test(k)) return norm.includes(k);
    return words.has(k);
  };
}

const anyMatch = (matcher, keywords) => keywords.some((k) => matcher(k));

// ── Category vocabularies ────────────────────────────────────────────────────
// Everything here is a CATEGORY name a restaurant would actually file a bottle
// under — on the category axis only, so food-ish collisions ("rye bread",
// "red wine vinegar") are an item-name concern and stay out of scope.

const LIQUOR_CATEGORY_KEYWORDS = [
  // The umbrella names
  'liquor', 'liquors', 'spirit', 'spirits', 'distilled', 'distilled spirits',
  'alcohol', 'booze', 'hard liquor', 'hard alcohol',
  // Bar-shelf tier names (safe on the category axis — nobody files food here)
  'well', 'well liquor', 'well spirits', 'rail', 'rail liquor', 'speed rail',
  'call', 'call liquor', 'premium liquor', 'top shelf', 'top-shelf', 'back bar',
  'bar liquor', 'bar spirits', 'house liquor', 'house spirits',
  // Spirit families
  'vodka', 'gin', 'tequila', 'mezcal', 'sotol', 'raicilla', 'rum', 'rhum',
  'whiskey', 'whisky', 'bourbon', 'scotch', 'rye', 'single malt', 'irish whiskey',
  'brandy', 'cognac', 'armagnac', 'calvados', 'grappa', 'pisco', 'eau de vie',
  'soju', 'shochu', 'baijiu', 'cachaca', 'aquavit', 'genever',
  // Liqueurs / modifiers
  'liqueur', 'liqueurs', 'cordial', 'cordials', 'aperitif', 'aperitivo',
  'digestif', 'digestivo', 'amaro', 'amari', 'vermouth', 'schnapps', 'absinthe',
  'ouzo', 'sambuca', 'arak', 'raki', 'pastis', 'anisette', 'limoncello',
  'triple sec', 'curacao', 'bitters', 'amaretto', 'kahlua', 'baileys',
  'irish cream', 'frangelico', 'chartreuse', 'benedictine', 'drambuie',
  'campari', 'aperol', 'fernet', 'jagermeister', 'galliano', 'chambord',
  'midori', 'cointreau', 'grand marnier', 'st germain', 'maraschino',
  'creme de', 'creme de cacao', 'creme de menthe',
  'orange liqueur', 'elderflower',
];

const WINE_CATEGORY_KEYWORDS = [
  // The umbrella names
  'wine', 'wines', 'vino', 'red wine', 'white wine', 'rose', 'rose wine',
  'blush', 'blush wine', 'sparkling', 'sparkling wine', 'bubbly',
  'champagne', 'prosecco', 'cava', 'cremant', 'lambrusco', 'brut',
  'house wine', 'btg', 'by the glass', 'by-the-glass', 'by the bottle',
  'wine bottle', 'bottle wine', 'bottled wine', 'dessert wine', 'fortified',
  'fortified wine', 'port', 'sherry', 'madeira', 'marsala', 'sake',
  'plum wine', 'ice wine', 'icewine', 'late harvest',
  // Varietal / region category names ("Cabernet", "Pinot Grigio", "Rioja")
  'cabernet', 'cabernet sauvignon', 'cabernet franc', 'sauvignon',
  'sauvignon blanc', 'sauv blanc', 'chardonnay', 'chard', 'pinot',
  'pinot noir', 'pinot grigio', 'pinot gris', 'grigio', 'merlot', 'malbec',
  'syrah', 'shiraz', 'zinfandel', 'zin', 'sangiovese', 'tempranillo',
  'nebbiolo', 'grenache', 'moscato', 'riesling', 'gewurztraminer', 'viognier',
  'chenin', 'chenin blanc', 'albarino', 'gruner', 'verdejo', 'semillon',
  'muscat', 'red blend', 'white blend', 'bordeaux', 'burgundy', 'chianti',
  'rioja', 'sancerre', 'barolo', 'brunello', 'chablis', 'beaujolais',
  'pinotage', 'super tuscan',
];

// HARD vetoes: the non-alcoholic shelf. Any hit ends the discussion —
// "Non-Alcoholic Wine" must never ride in on its wine token.
const HARD_EXCLUDED_CATEGORY_KEYWORDS = [
  'non alcoholic', 'non-alcoholic', 'alcohol free', 'alcohol-free',
  'zero proof', 'dealcoholized', 'mocktail', 'mocktails', 'virgin',
  'na beer', 'na wine', 'water', 'sparkling water', 'tonic', 'soda', 'juice',
  'coffee', 'tea', 'kombucha', 'energy', 'soft drink', 'soft drinks', 'pop',
  'mixer', 'mixers', 'syrup', 'syrups', 'grenadine', 'simple syrup',
  'sour mix', 'margarita mix', 'bloody mary mix', 'garnish', 'garnishes',
  'bar fruit', 'bar snacks', 'food', 'kitchen', 'produce', 'dry goods',
];

// SOFT beer-family signals. Beer is out of scope (whole cans/bottles, never a
// fill level), but only when nothing liquor/wine is said outright — a store's
// combined "Beer & Wine" or "Draft Wine" bucket still holds bottles worth
// sliding on. This list also vetoes beer categories that borrow wine words
// ("Brut IPA", "Sour Ale") when no liquor/wine umbrella word is present.
const SOFT_BEER_CATEGORY_KEYWORDS = [
  'beer', 'beers', 'ale', 'ales', 'lager', 'lagers', 'ipa', 'dipa', 'stout',
  'porter', 'pilsner', 'pils', 'cerveza', 'hefeweizen', 'hefe', 'witbier',
  'kolsch', 'saison', 'gose', 'bock', 'dunkel', 'lambic', 'marzen', 'helles',
  'pale ale', 'brown ale', 'amber ale', 'sour beer', 'craft beer',
  'seltzer', 'hard seltzer', 'hard tea', 'hard lemonade', 'hard kombucha',
  'cider', 'hard cider', 'rtd', 'ready to drink', 'canned cocktail',
  'keg', 'kegs', 'draft', 'drafts', 'draught', 'on tap', 'tap beer', 'mead',
];

// Umbrella words strong enough to keep a mixed bucket ("Beer & Wine",
// "Keg Wine", "Spirits & Beer") eligible despite a beer token.
const LIQUOR_WINE_UMBRELLA_KEYWORDS = [
  'liquor', 'liquors', 'spirit', 'spirits', 'wine', 'wines', 'champagne',
];

/**
 * Classify one category text. Returns 'liquor', 'wine', or null.
 * Hard vetoes ("Non-Alcoholic Wine", "Sparkling Water") always win; beer
 * alone is out; a beer token loses to an outright liquor/wine umbrella word
 * ("Beer & Wine"); varietal-named beers ("Brut IPA") stay out.
 */
export function bottleFamilyForCategoryText(categoryText) {
  if (!categoryText || !String(categoryText).trim()) return null;
  const match = makeMatcher(categoryText);
  if (anyMatch(match, HARD_EXCLUDED_CATEGORY_KEYWORDS)) return null;
  const liquor = anyMatch(match, LIQUOR_CATEGORY_KEYWORDS);
  const wine = anyMatch(match, WINE_CATEGORY_KEYWORDS);
  if (!liquor && !wine) return null;
  if (anyMatch(match, SOFT_BEER_CATEGORY_KEYWORDS)
      && !anyMatch(match, LIQUOR_WINE_UMBRELLA_KEYWORDS)) return null;
  return liquor ? 'liquor' : 'wine';
}

// Category-bearing fields a count line carries, most specific first. The
// P&L bucket ("Liquor" / "Wine" / "Beer") is an independent second signal.
function categoryTextsFor(itemLike) {
  if (!itemLike) return [];
  const snap = itemLike.item_snapshot || {};
  return [
    itemLike.category,
    itemLike.category_snapshot,
    snap.category,
    itemLike.pl_category_snapshot,
    snap.pl_category,
  ].filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
}

/**
 * Is this count line eligible for the bottle slider? Pure and synchronous —
 * reads only fields already on the count row (item_snapshot / snapshots), so
 * no fetch and no AI is ever involved.
 * @returns {{ eligible: boolean, family: 'liquor'|'wine'|null, matchedCategory: string|null }}
 */
export function bottleEligibilityForItem(itemLike) {
  for (const text of categoryTextsFor(itemLike)) {
    const family = bottleFamilyForCategoryText(text);
    if (family) return { eligible: true, family, matchedCategory: String(text) };
  }
  return { eligible: false, family: null, matchedCategory: null };
}

// ── Tenths math ──────────────────────────────────────────────────────────────

/** The slider snaps to tenths: 0 (empty) … 10 (full bottle). */
export const BOTTLE_TENTHS_MAX = 10;

function wholePartOf(countValue) {
  const n = Number.parseFloat(countValue);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/**
 * The decimal portion of a count as slider tenths (0–10). "5.72" → 7,
 * "0.95" → 10 (rounds to a full bottle), "5" → 0, "" → 0.
 */
export function tenthsFromCountValue(countValue) {
  const n = Number.parseFloat(countValue);
  if (!Number.isFinite(n) || n < 0) return 0;
  const frac = n - Math.floor(n);
  const tenths = Math.round(frac * 10);
  return Math.max(0, Math.min(BOTTLE_TENTHS_MAX, tenths));
}

/**
 * Replace the decimal portion of a count with the slider's tenths, keeping
 * the whole bottles. Built as a string so binary float never prints
 * "5.699999999999999".
 *   applyBottleTenths('5', 7)    → '5.7'
 *   applyBottleTenths('5.3', 7)  → '5.7'   (replaces, never adds)
 *   applyBottleTenths('', 9)     → '0.9'
 *   applyBottleTenths('5', 10)   → '6'     (a full bottle bumps the whole)
 *   applyBottleTenths('5.9', 0)  → '5'
 */
export function applyBottleTenths(countValue, tenths) {
  const t = Math.max(0, Math.min(BOTTLE_TENTHS_MAX, Math.round(Number(tenths) || 0)));
  const whole = wholePartOf(countValue);
  if (t >= BOTTLE_TENTHS_MAX) return String(whole + 1);
  if (t === 0) return String(whole);
  return `${whole}.${t}`;
}

/** True when the value carries a non-zero decimal fraction ("5.3", "0.9"). */
export function hasFractionalPart(countValue) {
  return tenthsFromCountValue(countValue) > 0;
}

/** Display text for a whole + tenths pair, e.g. (5, 7) → "5.7", (0, 0) → "0". */
export function formatBottleCount(whole, tenths) {
  return applyBottleTenths(String(Math.max(0, Math.floor(Number(whole) || 0))), tenths);
}
